
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Bell, Mail, Smartphone, CheckCircle } from "lucide-react";

interface NotificationPreferences {
  email_notifications: boolean;
  in_app_notifications: boolean;
  sms_notifications: boolean;
}

export const EmployeeNotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.employeeData?.id) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      
      // Get email notification preference
      const { data: emailPref } = await supabase
        .from('employee_notification_preferences')
        .select('*')
        .eq('employee_id', user?.employeeData?.id)
        .eq('notification_type', 'email')
        .eq('channel', 'email')
        .single();

      // Get in-app notification preference
      const { data: inAppPref } = await supabase
        .from('employee_notification_preferences')
        .select('*')
        .eq('employee_id', user?.employeeData?.id)
        .eq('notification_type', 'in_app')
        .eq('channel', 'in_app')
        .single();

      // Get SMS notification preference
      const { data: smsPref } = await supabase
        .from('employee_notification_preferences')
        .select('*')
        .eq('employee_id', user?.employeeData?.id)
        .eq('notification_type', 'sms')
        .eq('channel', 'sms')
        .single();

      const prefs: NotificationPreferences = {
        email_notifications: emailPref?.is_enabled ?? true,
        in_app_notifications: inAppPref?.is_enabled ?? true,
        sms_notifications: smsPref?.is_enabled ?? false,
      };
      
      setPreferences(prefs);

      // Create default preferences if they don't exist
      if (!emailPref || !inAppPref) {
        await createMissingPreferences(prefs);
      }
    } catch (err: any) {
      console.error('Error fetching preferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createMissingPreferences = async (prefs: NotificationPreferences) => {
    try {
      const preferencesToCreate = [];
      
      // Check and create email preference
      const { data: emailPref } = await supabase
        .from('employee_notification_preferences')
        .select('id')
        .eq('employee_id', user?.employeeData?.id)
        .eq('notification_type', 'email')
        .eq('channel', 'email')
        .single();

      if (!emailPref) {
        preferencesToCreate.push({
          employee_id: user?.employeeData?.id,
          notification_type: 'email',
          channel: 'email',
          is_enabled: prefs.email_notifications
        });
      }

      // Check and create in-app preference
      const { data: inAppPref } = await supabase
        .from('employee_notification_preferences')
        .select('id')
        .eq('employee_id', user?.employeeData?.id)
        .eq('notification_type', 'in_app')
        .eq('channel', 'in_app')
        .single();

      if (!inAppPref) {
        preferencesToCreate.push({
          employee_id: user?.employeeData?.id,
          notification_type: 'in_app',
          channel: 'in_app',
          is_enabled: prefs.in_app_notifications
        });
      }

      if (preferencesToCreate.length > 0) {
        await supabase
          .from('employee_notification_preferences')
          .insert(preferencesToCreate);
      }
    } catch (err: any) {
      console.error('Error creating default preferences:', err);
    }
  };

  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return;

    try {
      setSaving(true);
      
      // Update email preference if changed
      if ('email_notifications' in updates) {
        await supabase
          .from('employee_notification_preferences')
          .upsert({
            employee_id: user?.employeeData?.id,
            notification_type: 'email',
            channel: 'email',
            is_enabled: updates.email_notifications!
          }, {
            onConflict: 'employee_id,notification_type,channel'
          });
      }

      // Update in-app preference if changed
      if ('in_app_notifications' in updates) {
        await supabase
          .from('employee_notification_preferences')
          .upsert({
            employee_id: user?.employeeData?.id,
            notification_type: 'in_app',
            channel: 'in_app',
            is_enabled: updates.in_app_notifications!
          }, {
            onConflict: 'employee_id,notification_type,channel'
          });
      }

      // Update SMS preference if changed
      if ('sms_notifications' in updates) {
        await supabase
          .from('employee_notification_preferences')
          .upsert({
            employee_id: user?.employeeData?.id,
            notification_type: 'sms',
            channel: 'sms',
            is_enabled: updates.sms_notifications!
          }, {
            onConflict: 'employee_id,notification_type,channel'
          });
      }

      const updatedPrefs = { ...preferences, ...updates };
      setPreferences(updatedPrefs);
      toast.success("تم حفظ إعداداتك بنجاح!");
    } catch (err: any) {
      console.error('Error updating preferences:', err);
      toast.error("حدث خطأ أثناء حفظ الإعدادات");
    } finally {
      setSaving(false);
    }
  };

  if (!user?.employeeData?.id) {
    return (
      <Alert>
        <Bell className="h-4 w-4" />
        <AlertDescription>
          تحتاج إلى بيانات موظف صالحة لإدارة إعدادات التنبيهات.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            إعدادات التنبيهات الشخصية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">جاري التحميل...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          حدث خطأ في تحميل إعدادات التنبيهات: {error}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
      <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-notifications))]/20 to-transparent">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-notifications))] to-[hsl(var(--tab-notifications))]/80 shadow-lg shadow-[hsl(var(--tab-notifications))]/30">
            <Bell className="h-5 w-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">إعدادات التنبيهات الشخصية</span>
        </CardTitle>
        <CardDescription className="text-base">
          تحكم في كيفية تلقي التنبيهات عند تحديث حالات العملاء
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 p-6">
        <div className="grid gap-6">
          <div className="flex items-center justify-between p-4 border border-info/30 bg-gradient-to-br from-background to-info/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Bell className="h-5 w-5 text-info" />
              </div>
              <div>
                <Label className="text-base font-medium">التنبيهات الداخلية</Label>
                <p className="text-sm text-muted-foreground">تنبيهات فورية داخل النظام</p>
              </div>
            </div>
            <Switch
              checked={preferences?.in_app_notifications || false}
              onCheckedChange={(checked) => updatePreferences({ in_app_notifications: checked })}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-success/30 bg-gradient-to-br from-background to-success/5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Mail className="h-5 w-5 text-success" />
              </div>
              <div>
                <Label className="text-base font-medium">البريد الإلكتروني</Label>
                <p className="text-sm text-muted-foreground">تنبيهات عبر البريد الإلكتروني</p>
              </div>
            </div>
            <Switch
              checked={preferences?.email_notifications || false}
              onCheckedChange={(checked) => updatePreferences({ email_notifications: checked })}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-warning/30 bg-gradient-to-br from-background to-warning/5 rounded-xl shadow-md opacity-60">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <Smartphone className="h-5 w-5 text-warning" />
              </div>
              <div>
                <Label className="text-base font-medium">الرسائل النصية</Label>
                <p className="text-sm text-muted-foreground">تنبيهات عبر الرسائل النصية (قريباً)</p>
              </div>
            </div>
            <Switch
              checked={preferences?.sms_notifications || false}
              onCheckedChange={(checked) => updatePreferences({ sms_notifications: checked })}
              disabled={true}
            />
          </div>
        </div>

        {saving && (
          <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle className="h-5 w-5" />
              <p className="text-sm font-medium">جاري حفظ الإعدادات...</p>
            </div>
          </div>
        )}

        <div className="p-4 bg-gradient-to-br from-info/10 to-info/5 border border-info/20 rounded-xl shadow-md">
          <p className="font-medium mb-2 text-foreground flex items-center gap-2">
            <span className="text-info text-lg">💡</span>
            <span>ملاحظة:</span>
          </p>
          <p className="text-sm text-foreground/80">ستتلقى التنبيهات عند تحديث حالات العملاء المخصصين لك أو التي قمت بإنشائها، بناءً على إعدادات النظام.</p>
        </div>
      </CardContent>
    </Card>
  );
};
